import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import FileInput, { type FileInputHandle } from "@/components/ui/file-input";
import { Close, Plus } from "@/components/icons";
import { TIMESTAMP_REGEX } from "@/features/education-catalog/components/EducationChaptersSection/types";
import "../UnitAddModal/index.scss";

export type UnitVideoQuestionOption = {
  id: string;
  optionText: string;
  isCorrect: boolean;
};

export type UnitVideoQuestion = {
  id: string;
  questionText: string;
  timestamp: string; // "HH:MM:SS"
  options: UnitVideoQuestionOption[];
};

export interface UnitObjectiveItem {
  id: string;
  fileID: string;
  /** Yüklenen dosyanın MIME tipi (video soruları yalnızca video için gösterilir) */
  fileMimeType?: string;
  /** Yüklenen dosyanın orijinal adı (uzantı kontrolü için) */
  fileName?: string;
  /** Video içi sorular (opsiyonel) */
  videoQuestions?: UnitVideoQuestion[];
}

const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "webm",
  "mov",
  "mkv",
  "avi",
  "m4v",
  "ogv",
  "ogg",
]);

export function isVideoObjective(obj: UnitObjectiveItem): boolean {
  if (obj.fileMimeType?.startsWith("video/")) return true;
  const name = obj.fileName?.toLowerCase() ?? "";
  const dot = name.lastIndexOf(".");
  if (dot < 0) return false;
  return VIDEO_EXTENSIONS.has(name.slice(dot + 1));
}

const newId = () =>
  `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Trim/sanitize and drop fully-empty questions; returns objectives ready for submit. */
export function cleanObjectives(
  objectives: UnitObjectiveItem[],
): UnitObjectiveItem[] {
  const sanitize = (s: string) => (typeof s === "string" ? s.trim() : "");
  return objectives.map((obj) => {
    const qs = (obj.videoQuestions ?? [])
      .map((q) => {
        const questionText = sanitize(q.questionText);
        const timestamp = sanitize(q.timestamp);
        const options = (q.options ?? [])
          .map((o) => ({
            ...o,
            optionText: sanitize(o.optionText),
          }))
          .filter((o) => o.optionText.length > 0);
        const hasAny =
          questionText.length > 0 ||
          timestamp.length > 0 ||
          options.length > 0;
        if (!hasAny) return null;
        return {
          ...q,
          questionText,
          timestamp: timestamp || "00:00:00",
          options:
            options.length > 0
              ? options
              : [
                  { id: newId(), optionText: "", isCorrect: true },
                  { id: newId(), optionText: "", isCorrect: false },
                ],
        };
      })
      .filter((q): q is UnitVideoQuestion => !!q);
    return { ...obj, videoQuestions: qs.length > 0 ? qs : undefined };
  });
}

/** True if all video questions are well-formed (or no questions at all). */
export function areObjectiveQuestionsValid(
  objectives: UnitObjectiveItem[],
): boolean {
  const cleaned = cleanObjectives(objectives);
  const hasQuestions = cleaned.some(
    (o) => (o.videoQuestions?.length ?? 0) > 0,
  );
  if (!hasQuestions) return true;
  for (const obj of cleaned) {
    const seen = new Set<string>();
    for (const q of obj.videoQuestions ?? []) {
      if (!q.questionText.trim()) return false;
      if (!TIMESTAMP_REGEX.test(q.timestamp)) return false;
      if (seen.has(q.timestamp)) return false;
      seen.add(q.timestamp);
      if (!q.options || q.options.length < 2) return false;
      const correct = q.options.filter((o) => o.isCorrect).length;
      if (correct !== 1) return false;
      if (q.options.some((o) => !o.optionText.trim())) return false;
    }
  }
  return true;
}

export interface UnitContentEditorProps {
  objectives: UnitObjectiveItem[];
  onObjectivesChange: (next: UnitObjectiveItem[]) => void;
  supportedFormats?: string[];
  maxSize?: number;
}

const UnitContentEditor: React.FC<UnitContentEditorProps> = ({
  objectives,
  onObjectivesChange,
  supportedFormats = ["PDF", "DOC", "DOCX", "XLS", "XLSX"],
  maxSize = 500,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<FileInputHandle>(null);

  const setObjectives = (
    updater:
      | UnitObjectiveItem[]
      | ((prev: UnitObjectiveItem[]) => UnitObjectiveItem[]),
  ) => {
    onObjectivesChange(
      typeof updater === "function" ? updater(objectives) : updater,
    );
  };

  const handleUploadSuccess = (
    fileId: string,
    _previewUrl?: string,
    meta?: { fileName?: string; mimeType?: string },
  ) => {
    setObjectives((prev) => [
      ...prev,
      {
        id: `obj-${Date.now()}-${fileId}`,
        fileID: fileId,
        fileName: meta?.fileName,
        fileMimeType: meta?.mimeType,
        videoQuestions: [],
      },
    ]);
  };

  const handleFileRemovedFromUploader = (_index: number, fileId?: string) => {
    if (!fileId) return;
    setObjectives((prev) => prev.filter((o) => o.fileID !== fileId));
  };

  const updateObjective = (
    objectiveIndex: number,
    patch: Partial<UnitObjectiveItem>,
  ) => {
    setObjectives((prev) =>
      prev.map((o, i) => (i === objectiveIndex ? { ...o, ...patch } : o)),
    );
  };

  const removeObjective = (objectiveIndex: number) => {
    const target = objectives[objectiveIndex];
    if (target?.fileID && fileInputRef.current) {
      fileInputRef.current.removeFileById(target.fileID);
    }
    setObjectives((prev) =>
      target?.fileID
        ? prev.filter((o) => o.fileID !== target.fileID)
        : prev.filter((_, i) => i !== objectiveIndex),
    );
  };

  const addVideoQuestion = (objectiveIndex: number) => {
    const obj = objectives[objectiveIndex];
    if (!obj) return;
    const nextQ: UnitVideoQuestion = {
      id: newId(),
      questionText: "",
      timestamp: "00:00:00",
      options: [
        { id: newId(), optionText: "", isCorrect: true },
        { id: newId(), optionText: "", isCorrect: false },
      ],
    };
    updateObjective(objectiveIndex, {
      videoQuestions: [...(obj.videoQuestions ?? []), nextQ],
    });
  };

  const updateVideoQuestion = (
    objectiveIndex: number,
    questionIndex: number,
    patch: Partial<UnitVideoQuestion>,
  ) => {
    const obj = objectives[objectiveIndex];
    if (!obj) return;
    const next = (obj.videoQuestions ?? []).map((q, i) =>
      i === questionIndex ? { ...q, ...patch } : q,
    );
    updateObjective(objectiveIndex, { videoQuestions: next });
  };

  const removeVideoQuestion = (
    objectiveIndex: number,
    questionIndex: number,
  ) => {
    const obj = objectives[objectiveIndex];
    if (!obj) return;
    const next = (obj.videoQuestions ?? []).filter(
      (_, i) => i !== questionIndex,
    );
    updateObjective(objectiveIndex, { videoQuestions: next });
  };

  const updateVideoOption = (
    objectiveIndex: number,
    questionIndex: number,
    optionIndex: number,
    patch: Partial<UnitVideoQuestionOption>,
  ) => {
    const obj = objectives[objectiveIndex];
    const q = obj?.videoQuestions?.[questionIndex];
    if (!q) return;
    const options = q.options.map((opt, i) =>
      i === optionIndex ? { ...opt, ...patch } : opt,
    );
    updateVideoQuestion(objectiveIndex, questionIndex, { options });
  };

  const setOneCorrectOption = (
    objectiveIndex: number,
    questionIndex: number,
    correctOptionIndex: number,
  ) => {
    const obj = objectives[objectiveIndex];
    const q = obj?.videoQuestions?.[questionIndex];
    if (!q) return;
    const options = q.options.map((opt, i) => ({
      ...opt,
      isCorrect: i === correctOptionIndex,
    }));
    updateVideoQuestion(objectiveIndex, questionIndex, { options });
  };

  const addVideoOption = (objectiveIndex: number, questionIndex: number) => {
    const obj = objectives[objectiveIndex];
    const q = obj?.videoQuestions?.[questionIndex];
    if (!q) return;
    updateVideoQuestion(objectiveIndex, questionIndex, {
      options: [
        ...q.options,
        { id: newId(), optionText: "", isCorrect: false },
      ],
    });
  };

  const removeVideoOption = (
    objectiveIndex: number,
    questionIndex: number,
    optionIndex: number,
  ) => {
    const obj = objectives[objectiveIndex];
    const q = obj?.videoQuestions?.[questionIndex];
    if (!q || q.options.length <= 2) return;
    const options = q.options.filter((_, i) => i !== optionIndex);
    updateVideoQuestion(objectiveIndex, questionIndex, { options });
  };

  return (
    <>
      <FileInput
        ref={fileInputRef}
        label={t("createEducation.unitModal.uploadDocument") + "*"}
        placeholder={t("createEducation.unitModal.dropzoneText")}
        accept={
          supportedFormats.map((f) => `.${f.toLowerCase()}`).join(",") +
          ",.mp4,.webm,video/*"
        }
        supportedFormats={[...supportedFormats, "MP4", "WEBM"]}
        maxSize={maxSize}
        maxFiles={10}
        uploadToBackend
        onUploadSuccess={handleUploadSuccess}
        onFileRemove={handleFileRemovedFromUploader}
        enableDemoProgress={false}
        fileType="file"
      />
      <div className="unit-add-modal-question-section">
        <div className="unit-add-modal-question-section-title">
          {t("createEducation.unitModal.questionsTitle")}
          <span className="unit-add-modal-question-section-subtitle">
            {t("createEducation.unitModal.optional")}
          </span>
        </div>

        {objectives.length === 0 ? (
          <p className="unit-add-modal-muted">
            {t("createEducation.unitModal.questionsHintBeforeUpload")}
          </p>
        ) : (
          <div className="unit-add-modal-files">
            <p className="unit-add-modal-muted">
              {t(
                "createEducation.unitModal.uploadedCount",
                t("unitModal.uploadedCount"),
                { count: objectives.length },
              )}
            </p>

            <div className="unit-add-modal-objectives">
              {objectives.map((obj, objIdx) => {
                const isVideo = isVideoObjective(obj);
                return (
                  <div key={obj.id} className="unit-add-modal-objective">
                    <div className="unit-add-modal-objective-header">
                      <div className="unit-add-modal-objective-title">
                        {t("createEducation.unitModal.file")} {objIdx + 1}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {isVideo && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<Plus width={16} height={16} />}
                            onClick={() => addVideoQuestion(objIdx)}
                          >
                            {t(
                              "createEducation.unitModal.addVideoQuestion",
                            )}
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Close width={16} height={16} />}
                          onClick={() => removeObjective(objIdx)}
                        >
                          {t("createEducation.unitModal.removeFile")}
                        </Button>
                      </div>
                    </div>

                    {isVideo && (
                      <div className="unit-add-modal-questions">
                        {(obj.videoQuestions ?? []).length === 0 ? (
                          <p className="unit-add-modal-muted">
                            {t(
                              "createEducation.unitModal.questionsOptional",
                            )}
                          </p>
                        ) : (
                          (obj.videoQuestions ?? []).map((q, qIdx) => (
                            <div
                              key={q.id}
                              className="unit-add-modal-question"
                            >
                              <Input
                                placeholder={t(
                                  "createEducation.unitModal.questionText",
                                )}
                                value={q.questionText}
                                onChange={(e) =>
                                  updateVideoQuestion(objIdx, qIdx, {
                                    questionText: e.target.value,
                                  })
                                }
                              />
                              <Input
                                placeholder="HH:MM:SS"
                                value={q.timestamp}
                                onChange={(e) =>
                                  updateVideoQuestion(objIdx, qIdx, {
                                    timestamp: e.target.value,
                                  })
                                }
                                style={{ marginTop: 6 }}
                              />
                              <p className="unit-add-modal-hint">
                                {t(
                                  "createEducation.unitModal.timestampFormat",
                                )}
                              </p>
                              {TIMESTAMP_REGEX.test(q.timestamp) &&
                                (obj.videoQuestions ?? []).some(
                                  (other, otherIdx) =>
                                    otherIdx !== qIdx &&
                                    other.timestamp === q.timestamp,
                                ) && (
                                  <p
                                    className="unit-add-modal-hint"
                                    style={{ color: "#dc2626" }}
                                  >
                                    {t(
                                      "createEducation.unitModal.duplicateTimestamp",
                                    )}
                                  </p>
                                )}

                              <div className="unit-add-modal-options">
                                {q.options.map((opt, optIdx) => (
                                  <div
                                    key={opt.id}
                                    className="unit-add-modal-option-row"
                                  >
                                    <input
                                      type="radio"
                                      name={`unit-q-${objIdx}-${qIdx}`}
                                      checked={opt.isCorrect}
                                      onChange={() =>
                                        setOneCorrectOption(
                                          objIdx,
                                          qIdx,
                                          optIdx,
                                        )
                                      }
                                    />
                                    <Input
                                      placeholder={t(
                                        "createEducation.unitModal.optionText",
                                      )}
                                      value={opt.optionText}
                                      onChange={(e) =>
                                        updateVideoOption(
                                          objIdx,
                                          qIdx,
                                          optIdx,
                                          {
                                            optionText: e.target.value,
                                          },
                                        )
                                      }
                                      style={{ flex: 1 }}
                                    />
                                    <button
                                      type="button"
                                      className="unit-add-modal-icon-btn"
                                      onClick={() =>
                                        removeVideoOption(
                                          objIdx,
                                          qIdx,
                                          optIdx,
                                        )
                                      }
                                      disabled={q.options.length <= 2}
                                      aria-label={t(
                                        "createEducation.unitModal.removeOption",
                                      )}
                                    >
                                      <Close width={16} height={16} />
                                    </button>
                                  </div>
                                ))}
                                <Button
                                  variant="outlined"
                                  color="default"
                                  size="small"
                                  style={{ marginTop: 6 }}
                                  onClick={() =>
                                    addVideoOption(objIdx, qIdx)
                                  }
                                >
                                  {t(
                                    "createEducation.unitModal.addOption",
                                  )}
                                </Button>
                              </div>

                              <button
                                type="button"
                                className="unit-add-modal-remove-link"
                                onClick={() =>
                                  removeVideoQuestion(objIdx, qIdx)
                                }
                              >
                                {t(
                                  "createEducation.unitModal.removeQuestion",
                                )}
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UnitContentEditor;
